import { z } from 'zod';
import { moneyMinorSchema } from '../api';
import { TAX_CATEGORIES } from '../countries/types';
import { RESIDENT_KINDS } from '../countries/tax';
import { DOCTOR_DISCOUNT_KINDS } from '../doctor-discount';
import { withoutBidiControls } from '../text';
import { serviceImagingDetailsSchema, serviceLabDetailsSchema } from '../orderables';
import { COLLECTABLE_PAYMENT_MODE, CREDIT_NOTE_SETTLEMENT, CREDIT_NOTE_STATUS, DEPOSIT_PAYMENT_MODE, MANUAL_DEPOSIT_TYPE, INVOICE_CONTEXT, INVOICE_STATUS, PATIENT_CATEGORY, PAYMENT_MODE, REFUND_STATUS, } from '../enums';
const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-25');
export const upsertChargeItemSchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(24),
    name: z.preprocess(withoutBidiControls, z.string().trim().min(2).max(200)),
    nameLocal: z.preprocess((value) => (value === '' ? null : withoutBidiControls(value)), z.string().trim().max(200).nullable().optional()),
    departmentId: z.string().uuid().nullable().optional(),
    departmentIds: z.array(z.string().uuid()).max(50).optional(),
    branchId: z.string().uuid().nullable().optional(),
    category: z.enum([
        'consultation',
        'procedure',
        'bed',
        'nursing',
        'ot',
        'lab',
        'radiology',
        'pharmacy',
        'package',
        'misc',
    ]),
    taxPct: z.number().min(0).max(100).default(0),
    taxCategory: z.enum(TAX_CATEGORIES).nullable().optional(),
    benefitCategory: z.string().trim().min(2).max(60).nullable().optional(),
    durationMinutes: z.number().int().min(0).max(1440).default(0),
    nphiesClaimable: z.boolean().default(false),
    isActive: z.boolean().default(true),
    /** Base price used when no tariff row matches. */
    basePriceMinor: moneyMinorSchema.min(0),
    labTest: serviceLabDetailsSchema.nullable().optional(),
    imaging: serviceImagingDetailsSchema.nullable().optional(),
});
export const chargeItemDoctorRowSchema = z
    .object({
    doctorId: z.string().uuid(),
    discountKind: z.enum(DOCTOR_DISCOUNT_KINDS),
    discountValue: z.number().min(0),
    effectiveFrom: dateOnlySchema.optional(),
})
    .superRefine((value, ctx) => {
    if (value.discountKind === 'percent' && value.discountValue > 100) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['discountValue'], message: 'A percentage discount cannot be above 100' });
    }
    if (value.discountKind === 'fixed' && !Number.isInteger(value.discountValue)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['discountValue'], message: 'A fixed discount is a whole number of minor units' });
    }
});
export const setChargeItemDoctorsSchema = z.object({
    doctors: z.array(chargeItemDoctorRowSchema).max(100),
});
export const branchTaxRateInputSchema = z.object({
    taxCategory: z.enum(TAX_CATEGORIES),
    residentKind: z.enum(RESIDENT_KINDS),
    pct: z.number().min(0).max(100),
});
export const setBranchTaxRatesSchema = z.object({
    effectiveFrom: dateOnlySchema,
    rates: z.array(branchTaxRateInputSchema).min(1).max(TAX_CATEGORIES.length * RESIDENT_KINDS.length),
});
export const PACKAGE_PRICING = ['manual', 'sum'];
export const TARIFF_KINDS = ['custom', 'nphies'];
export const setNphiesPriceSchema = z
    .object({
    claimable: z.boolean().optional(),
    priceMinor: moneyMinorSchema.min(0).nullable().optional(),
})
    .refine((value) => value.claimable !== undefined || value.priceMinor !== undefined, {
    message: 'Send the claimable flag, the NPHIES price, or both',
    path: ['priceMinor'],
});
export const upsertTariffSchema = z
    .object({
    code: z.string().trim().toUpperCase().min(2).max(24),
    name: z.string().trim().min(2).max(160),
    /** Patient categories this tariff serves; the first match wins. */
    categories: z.array(z.enum(PATIENT_CATEGORY)).default([]),
    payerId: z.string().uuid().nullable().optional(),
    planId: z.string().uuid().nullable().optional(),
    forBranchId: z.string().uuid().nullable().optional(),
    validFrom: dateOnlySchema.nullable().optional(),
    validTo: dateOnlySchema.nullable().optional(),
    priority: z.number().int().min(0).max(1000).default(100),
    isActive: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    if (value.categories.length === 0 && !value.payerId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['categories'], message: 'Pick the patient categories this tariff serves, or attach it to a payer' });
    }
    if (value.planId && !value.payerId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['planId'], message: 'A plan price list belongs to the plan’s payer; choose the payer too' });
    }
    if (value.validFrom && value.validTo && value.validTo < value.validFrom) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['validTo'], message: 'The price list ends before it starts' });
    }
});
export const setTariffPriceSchema = z.object({
    chargeItemId: z.string().uuid(),
    priceMinor: moneyMinorSchema.min(0),
    discountPct: z.number().min(0).max(100).default(0),
});
export const invoiceLineSchema = z.object({
    chargeItemId: z.string().uuid().nullable().optional(),
    productId: z.string().uuid().nullable().optional(),
    stockBatchId: z.string().uuid().nullable().optional(),
    description: z.string().trim().min(1).max(240),
    qty: z.number().min(0.001).max(100000),
    unitPriceMinor: moneyMinorSchema.min(0).optional(),
    discountPct: z.number().min(0).max(100).default(0),
    discountMinor: moneyMinorSchema.min(0).optional(),
    taxPct: z.number().min(0).max(100).optional(),
    performedByUserId: z.string().uuid().nullable().optional(),
    notes: z.string().max(400).optional(),
});
export const invoicePackageSchema = z.object({
    packageId: z.string().uuid(),
    performedByUserId: z.string().uuid().nullable().optional(),
});
export const createInvoiceSchema = z
    .object({
    patientId: z.string().uuid().nullable().optional(),
    /** Walk-in pharmacy customers may have no MRN at all. */
    walkInName: z.string().trim().max(160).optional(),
    walkInPhone: z.string().trim().max(20).optional(),
    branchId: z.string().uuid().optional(),
    context: z.enum(INVOICE_CONTEXT).default('opd'),
    encounterId: z.string().uuid().nullable().optional(),
    appointmentId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    payerId: z.string().uuid().nullable().optional(),
    lines: z.array(invoiceLineSchema).max(200),
    packages: z.array(invoicePackageSchema).max(50).optional(),
    discountReason: z.string().trim().max(300).optional(),
    notes: z.string().max(1000).optional(),
    /** Issue immediately instead of leaving the invoice in draft. */
    issue: z.boolean().default(true),
    requestDiscountApproval: z.boolean().optional(),
    secondConsultationReason: z.string().trim().min(5).max(300).optional(),
})
    .superRefine((value, ctx) => {
    if (value.lines.length === 0 && (value.packages?.length ?? 0) === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['lines'], message: 'Add at least one service or package' });
    }
});
export const invoiceQuerySchema = z.object({
    patientId: z.string().uuid().optional(),
    admissionId: z.string().uuid().optional(),
    encounterId: z.string().uuid().optional(),
    context: z.enum(INVOICE_CONTEXT).optional(),
    status: z.enum(INVOICE_STATUS).optional(),
    branchId: z.string().uuid().optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
export const collectPaymentSchema = z.object({
    invoiceId: z.string().uuid().nullable().optional(),
    admissionId: z.string().uuid().nullable().optional(),
    amountMinor: moneyMinorSchema.min(1),
    mode: z.enum(COLLECTABLE_PAYMENT_MODE),
    txnRef: z.string().trim().max(120).optional(),
    /** Cash tendered, used to compute change at the counter. */
    tenderedMinor: moneyMinorSchema.min(0).optional(),
    notes: z.string().max(400).optional(),
});
export const requestRefundSchema = z.object({
    paymentId: z.string().uuid(),
    amountMinor: moneyMinorSchema.min(1),
    reason: z.string().trim().min(5).max(400),
});
export const approveRefundSchema = z
    .object({
    decision: z.enum(['approved', 'rejected']),
    notes: z.string().trim().max(400).optional(),
})
    .superRefine((value, ctx) => {
    if (value.decision === 'rejected' && (value.notes ?? '').length < 3) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['notes'], message: 'Say why the request is rejected' });
    }
});
export const refundListQuerySchema = z.object({
    status: z.enum(REFUND_STATUS).optional(),
    mode: z.enum(PAYMENT_MODE).optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
});
export const removeInvoiceLineSchema = z.object({
    reason: z.string().trim().min(5).max(400),
});
export const overrideLineTaxSchema = z.object({
    category: z.enum(['standard', 'zero', 'exempt', 'out_of_scope']),
    reason: z.string().trim().min(5).max(400),
});
export const createCreditNoteSchema = z
    .object({
    lines: z
        .array(z.object({
        invoiceItemId: z.string().uuid(),
        qty: z.number().positive().max(100000),
    }))
        .max(200)
        .optional(),
    amountMinor: moneyMinorSchema.min(1).optional(),
    reason: z.string().trim().min(5).max(400),
    settlement: z.enum(CREDIT_NOTE_SETTLEMENT).default('none'),
})
    .superRefine((value, ctx) => {
    const byLines = (value.lines?.length ?? 0) > 0;
    const byAmount = value.amountMinor !== undefined;
    if (byLines === byAmount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['lines'],
            message: 'Choose lines to credit, or enter one amount, not both and not neither',
        });
    }
});
export const creditNoteDecisionSchema = approveRefundSchema;
export const creditNoteListQuerySchema = z.object({
    status: z.enum(CREDIT_NOTE_STATUS).optional(),
    invoiceId: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
});
export const statementQuerySchema = z
    .object({
    patientId: z.string().uuid().optional(),
    encounterId: z.string().uuid().optional(),
    admissionId: z.string().uuid().optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    size: z.enum(['A4', 'thermal']).default('A4'),
})
    .refine((value) => Boolean(value.patientId || value.encounterId || value.admissionId), {
    message: 'Say whose statement this is: a patient, an encounter or an admission',
    path: ['patientId'],
});
export const receiptKindSchema = z.enum(['payment', 'deposit', 'refund']);
export const customerCreditQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
});
export const customerAccountQuerySchema = z.object({
    name: z.string().trim().min(1).max(160),
    phone: z.string().trim().max(20).optional(),
    branchId: z.string().uuid().optional(),
});
export const ON_ACCOUNT_INVOICE_LIMIT = 500;
export const collectOnAccountSchema = z
    .object({
    customerName: z.string().trim().min(1).max(160),
    customerPhone: z.string().trim().max(20).optional(),
    branchId: z.string().uuid().optional(),
    invoiceIds: z.array(z.string().uuid()).min(1).max(ON_ACCOUNT_INVOICE_LIMIT).optional(),
    amountMinor: moneyMinorSchema.min(1),
    mode: z.enum(DEPOSIT_PAYMENT_MODE),
    txnRef: z.string().trim().max(120).optional(),
    tenderedMinor: moneyMinorSchema.min(0).optional(),
    notes: z.string().max(400).optional(),
})
    .refine((input) => Boolean(input.customerPhone?.trim()) || Boolean(input.invoiceIds?.length), {
    message: 'A customer with no phone number on file is identified by the sales being paid: pick them',
    path: ['invoiceIds'],
});
export const openShiftSchema = z.object({
    branchId: z.string().uuid().optional(),
    counterName: z.string().trim().max(80).default('Main Counter'),
    openingFloatMinor: moneyMinorSchema.min(0).default(0),
});
export const closeShiftSchema = z.object({
    countedCashMinor: moneyMinorSchema.min(0),
    denominations: z.record(z.string(), z.number().int().min(0)).optional(),
    looseCashMinor: moneyMinorSchema.min(0).optional(),
    notes: z.string().max(600).optional(),
});
export const depositSchema = z.object({
    admissionId: z.string().uuid(),
    amountMinor: moneyMinorSchema.min(1),
    type: z.enum(MANUAL_DEPOSIT_TYPE).default('advance'),
    mode: z.enum(DEPOSIT_PAYMENT_MODE).default('cash'),
    txnRef: z.string().trim().max(120).optional(),
    notes: z.string().max(400).optional(),
});
//# sourceMappingURL=billing.js.map